import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { trackingApi, rateApi } from '../services/api';

/**
 * CargoPulse public landing page.
 * Visible to logged-out visitors at /cargopulse/.
 * Sections: header > hero (with track widget) > why ECL > rate quote > footer.
 *
 * Backend endpoints used (public, no auth):
 *   - GET  /api/v1/track/:trackingId       -> public tracking lookup
 *   - POST /api/v1/rate-calculator         -> rate calculation
 */

export default function LandingPage() {
  const navigate = useNavigate();
  const [trackingId, setTrackingId] = useState('');
  const [trackBusy, setTrackBusy] = useState(false);

  // rate quote state
  const [quote, setQuote] = useState({
    origin_pincode: '75600',
    dest_pincode:   '54000',
    weight:         '',
    service_type:   'DOM_STD',
    cod_amount:     '',
  });
  const [quoteResult, setQuoteResult] = useState(null);
  const [quoteBusy, setQuoteBusy] = useState(false);

  // staggered fade-in on mount
  useEffect(() => {
    document.body.classList.add('lp-mounted');
    return () => document.body.classList.remove('lp-mounted');
  }, []);

  const onTrack = e => {
    e.preventDefault();
    const id = trackingId.trim();
    if (!id) { toast.error('Enter a tracking ID'); return; }
    navigate(`/track/${encodeURIComponent(id)}`);
  };

  const setQ = k => e => setQuote(q => ({ ...q, [k]: e.target.value }));

  const onQuote = async e => {
    e.preventDefault();
    if (!quote.weight || Number(quote.weight) <= 0) { toast.error('Enter weight in kg'); return; }
    setQuoteBusy(true);
    setQuoteResult(null);
    try {
      const res = await rateApi.calculate({
        origin_pincode: quote.origin_pincode,
        dest_pincode:   quote.dest_pincode,
        weight:         Number(quote.weight),
        service_type:   quote.service_type,
        cod_amount:     quote.cod_amount ? Number(quote.cod_amount) : 0,
      });
      if (!res.success) throw new Error(res.message || 'Could not calculate rate');
      setQuoteResult(res.data);
    } catch (err) {
      toast.error(err.message || 'Rate calculator unavailable');
    } finally {
      setQuoteBusy(false);
    }
  };

  return (
    <div className="lp">
      {/* --- HEADER --- */}
      <header className="lp-nav">
        <div className="lp-nav-inner">
          <Link to="/" className="lp-brand">
            <span className="lp-brand-mark">{'\u26A1'}</span>
            <span className="lp-brand-name">CargoPulse</span>
          </Link>
          <nav className="lp-nav-links">
            <a href="#track">Track</a>
            <a href="#quote">Pricing</a>
            <a href="#why">Why us</a>
            <Link to="/login" className="lp-nav-signin">Sign in</Link>
            <Link to="/register" className="lp-nav-cta">Create account</Link>
          </nav>
        </div>
      </header>

      {/* --- HERO --- */}
      <section className="lp-hero" id="track">
        <div className="lp-hero-grid">
          <div className="lp-hero-copy">
            <span className="lp-eyebrow">PAKISTAN'S MODERN LOGISTICS PLATFORM</span>
            <h1 className="lp-h1">
              Smart cargo &amp; courier<br/>
              <em>delivered with precision.</em>
            </h1>
            <p className="lp-lede">
              Same-day to standard delivery, real-time tracking, COD support, and a corporate billing portal {'\u2014'} built for individuals and businesses across Pakistan.
            </p>
            <div className="lp-hero-ctas">
              <Link to="/register" className="lp-btn lp-btn-primary">
                <span>Get started {'\u2014'} free</span>
                <span className="lp-btn-arrow">{'\u2192'}</span>
              </Link>
              <Link to="/login" className="lp-btn lp-btn-ghost">I already have an account</Link>
            </div>
          </div>

          {/* Track widget */}
          <form onSubmit={onTrack} className="lp-track-card">
            <div className="lp-track-label">TRACK A SHIPMENT</div>
            <div className="lp-track-row">
              <input
                type="text"
                value={trackingId}
                onChange={e => setTrackingId(e.target.value.toUpperCase())}
                placeholder="ECLPK20260509000001"
                className="lp-track-input"
                autoComplete="off"
              />
              <button type="submit" className="lp-track-btn" disabled={trackBusy}>
                {trackBusy ? '...' : <>Track {'\u2192'}</>}
              </button>
            </div>
            <div className="lp-track-hint">
              Enter your tracking ID {'\u2014'} no login required. <Link to="/track">Trouble tracking?</Link>
            </div>
          </form>
        </div>
      </section>

      {/* --- WHY ECL --- */}
      <section className="lp-why" id="why">
        <div className="lp-section-inner">
          <span className="lp-eyebrow lp-eyebrow-light">WHY ECL</span>
          <h2 className="lp-h2">Built for how Pakistan actually ships.</h2>

          <div className="lp-why-grid">
            <div className="lp-why-item">
              <div className="lp-why-num">01</div>
              <h3>Four delivery speeds</h3>
              <p>Same-day, Express, Standard, and Economy {'\u2014'} pick what fits the urgency and the budget. Domestic and international lanes.</p>
            </div>
            <div className="lp-why-item">
              <div className="lp-why-num">02</div>
              <h3>Transparent pricing</h3>
              <p>Calculate your rate online before you book. Volumetric weight, fuel surcharge, COD fee, and tax {'\u2014'} every line itemised.</p>
            </div>
            <div className="lp-why-item">
              <div className="lp-why-num">03</div>
              <h3>Real-time tracking</h3>
              <p>Every scan, every hub, every status {'\u2014'} visible the moment it happens. SMS, email, and WhatsApp notifications.</p>
            </div>
            <div className="lp-why-item">
              <div className="lp-why-num">04</div>
              <h3>Cash on Delivery</h3>
              <p>Collect payment from your receiver at the doorstep. OTP-secured, e-receipted, T+1 remitted to you.</p>
            </div>
            <div className="lp-why-item">
              <div className="lp-why-num">05</div>
              <h3>Corporate accounts</h3>
              <p>Credit terms, consolidated billing, contracted rates, API and Shopify/WooCommerce integrations.</p>
            </div>
            <div className="lp-why-item">
              <div className="lp-why-num">06</div>
              <h3>Proof of delivery</h3>
              <p>Signature plus photo at the doorstep. Stored for 7 years, downloadable as PDF {'\u2014'} proof that holds up.</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- RATE QUOTE --- */}
      <section className="lp-quote" id="quote">
        <div className="lp-section-inner lp-quote-inner">
          <div className="lp-quote-copy">
            <span className="lp-eyebrow">QUICK QUOTE</span>
            <h2 className="lp-h2 lp-h2-light">Know the price<br/>before you ship.</h2>
            <p className="lp-quote-lede">
              No login, no commitment. See your rate in real time using the same tariff engine that powers customer bookings.
            </p>
          </div>

          <form onSubmit={onQuote} className="lp-quote-card">
            <div className="lp-quote-row">
              <div className="lp-q-field">
                <label>From</label>
                <select value={quote.origin_pincode} onChange={setQ('origin_pincode')}>
                  <option value="75600">Karachi (75600)</option>
                  <option value="75500">Karachi SITE (75500)</option>
                  <option value="54000">Lahore Gulberg (54000)</option>
                  <option value="44000">Islamabad (44000)</option>
                </select>
              </div>
              <div className="lp-q-field">
                <label>To</label>
                <select value={quote.dest_pincode} onChange={setQ('dest_pincode')}>
                  <option value="54000">Lahore Gulberg (54000)</option>
                  <option value="75600">Karachi Clifton (75600)</option>
                  <option value="44000">Islamabad (44000)</option>
                  <option value="46000">Rawalpindi (46000)</option>
                </select>
              </div>
            </div>

            <div className="lp-quote-row">
              <div className="lp-q-field">
                <label>Weight (kg)</label>
                <input type="number" step="0.1" min="0.1" placeholder="2.5"
                       value={quote.weight} onChange={setQ('weight')} />
              </div>
              <div className="lp-q-field">
                <label>Service</label>
                <select value={quote.service_type} onChange={setQ('service_type')}>
                  <option value="DOM_SD">Same-Day</option>
                  <option value="DOM_EXP">Express (next-day)</option>
                  <option value="DOM_STD">Standard (2 days)</option>
                  <option value="DOM_ECO">Economy</option>
                </select>
              </div>
            </div>

            <div className="lp-q-field lp-q-field-full">
              <label>COD amount (optional)</label>
              <input type="number" min="0" placeholder="0"
                     value={quote.cod_amount} onChange={setQ('cod_amount')} />
            </div>

            <button type="submit" className="lp-quote-btn" disabled={quoteBusy}>
              {quoteBusy ? 'Calculating...' : 'Calculate rate'}
            </button>

            {quoteResult && (
              <div className="lp-quote-result">
                <div className="lp-qr-line">
                  <span>Base freight</span>
                  <span>PKR {Number(quoteResult.base_freight ?? 0).toFixed(0)}</span>
                </div>
                <div className="lp-qr-line">
                  <span>Fuel surcharge</span>
                  <span>PKR {Number(quoteResult.fuel_surcharge ?? 0).toFixed(0)}</span>
                </div>
                {Number(quoteResult.cod_fee || 0) > 0 && (
                  <div className="lp-qr-line">
                    <span>COD fee</span>
                    <span>PKR {Number(quoteResult.cod_fee).toFixed(0)}</span>
                  </div>
                )}
                <div className="lp-qr-line">
                  <span>Tax</span>
                  <span>PKR {Number(quoteResult.tax_amount ?? 0).toFixed(0)}</span>
                </div>
                <div className="lp-qr-line lp-qr-total">
                  <span>Total</span>
                  <span>PKR {Number(quoteResult.total_charges ?? 0).toFixed(0)}</span>
                </div>
                <Link to="/register" className="lp-qr-book">
                  Book this shipment {'\u2192'}
                </Link>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <span className="lp-brand-mark">{'\u26A1'}</span>
            <span className="lp-brand-name">CargoPulse</span>
            <p className="lp-footer-tag">A product of ECL {'\u2014'} Express Courier &amp; Logistics</p>
          </div>
          <div className="lp-footer-cols">
            <div>
              <h4>Product</h4>
              <a href="#track">Track</a>
              <a href="#quote">Pricing</a>
              <Link to="/register">Sign up</Link>
              <Link to="/login">Sign in</Link>
            </div>
            <div>
              <h4>Company</h4>
              <a href="mailto:contact@axotech.net">Contact</a>
              <a href="#">About</a>
              <a href="#">Careers</a>
            </div>
            <div>
              <h4>Legal</h4>
              <a href="#">Terms</a>
              <a href="#">Privacy</a>
              <a href="#">Cookie policy</a>
            </div>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span>{'\u00A9'} {new Date().getFullYear()} ECL. All rights reserved.</span>
          <span>Karachi {'\u00B7'} Lahore {'\u00B7'} Islamabad</span>
        </div>
      </footer>

      {/* --- STYLES --- */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Inter+Tight:wght@400;500;600;700&display=swap');

        .lp {
          --navy:        #0A1838;
          --navy-deep:   #050D1A;
          --amber:       #F59E0B;
          --amber-soft:  #FCD34D;
          --cream:       #FAF7F2;
          --ink:         #1F2937;
          --muted:       #6B7280;
          --line:        #E5E7EB;
          --serif:       'DM Serif Display', Georgia, serif;
          --sans:        'Inter Tight', -apple-system, sans-serif;
          font-family: var(--sans);
          color: var(--ink);
          background: var(--cream);
          min-height: 100vh;
          line-height: 1.55;
        }

        body.lp-mounted .lp-hero-copy > *,
        body.lp-mounted .lp-track-card { opacity: 1; transform: translateY(0); }
        .lp-hero-copy > *, .lp-track-card {
          opacity: 0; transform: translateY(12px);
          transition: opacity .6s cubic-bezier(.2,.8,.2,1), transform .6s cubic-bezier(.2,.8,.2,1);
        }
        .lp-hero-copy > *:nth-child(1) { transition-delay: .05s; }
        .lp-hero-copy > *:nth-child(2) { transition-delay: .15s; }
        .lp-hero-copy > *:nth-child(3) { transition-delay: .25s; }
        .lp-hero-copy > *:nth-child(4) { transition-delay: .35s; }
        .lp-track-card { transition-delay: .45s; }

        .lp a { color: inherit; text-decoration: none; }

        .lp-nav {
          position: sticky; top: 0; z-index: 50;
          background: rgba(250, 247, 242, .92);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--line);
        }
        .lp-nav-inner {
          max-width: 1200px; margin: 0 auto;
          display: flex; align-items: center; justify-content: space-between;
          padding: 18px 32px;
        }
        .lp-brand { display: flex; align-items: center; gap: 10px; font-weight: 700; }
        .lp-brand-mark { font-size: 1.4rem; color: var(--amber); }
        .lp-brand-name { font-size: 1.05rem; letter-spacing: -.01em; color: var(--navy); }
        .lp-nav-links { display: flex; align-items: center; gap: 28px; }
        .lp-nav-links a { font-size: .92rem; color: var(--muted); transition: color .2s; }
        .lp-nav-links a:hover { color: var(--navy); }
        .lp-nav-signin { color: var(--navy) !important; font-weight: 500; }
        .lp-nav-cta {
          padding: 9px 18px; background: var(--navy); color: #fff !important;
          border-radius: 999px; font-weight: 500; font-size: .88rem;
          transition: transform .2s, background .2s;
        }
        .lp-nav-cta:hover { background: var(--navy-deep); transform: translateY(-1px); }

        .lp-hero {
          padding: 80px 32px 100px;
          background: linear-gradient(180deg, var(--cream) 0%, #F1ECDF 100%);
          position: relative; overflow: hidden;
        }
        .lp-hero::before {
          content: ''; position: absolute;
          top: -200px; right: -200px;
          width: 600px; height: 600px;
          background: radial-gradient(circle, rgba(245, 158, 11, .08), transparent 70%);
          pointer-events: none;
        }
        .lp-hero-grid {
          max-width: 1200px; margin: 0 auto;
          display: grid; grid-template-columns: 1.3fr 1fr; gap: 64px;
          align-items: center;
        }
        .lp-eyebrow {
          display: inline-block;
          font-size: .72rem; letter-spacing: .15em; font-weight: 600;
          color: var(--amber); margin-bottom: 18px;
        }
        .lp-eyebrow-light { color: var(--amber-soft); }
        .lp-h1 {
          font-family: var(--serif); font-weight: 400;
          font-size: clamp(2.4rem, 5.5vw, 4.4rem);
          line-height: 1.04; letter-spacing: -.02em;
          color: var(--navy); margin: 0 0 24px;
        }
        .lp-h1 em { font-style: italic; color: var(--amber); }
        .lp-lede {
          font-size: 1.08rem; color: var(--muted);
          max-width: 520px; margin: 0 0 32px;
        }
        .lp-hero-ctas { display: flex; gap: 14px; flex-wrap: wrap; }
        .lp-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 14px 24px; border-radius: 999px;
          font-weight: 500; font-size: .95rem;
          transition: transform .2s, background .2s, box-shadow .2s;
        }
        .lp-btn-primary {
          background: var(--navy); color: #fff;
          box-shadow: 0 4px 14px rgba(10, 24, 56, .25);
        }
        .lp-btn-primary span { color: #fff; }
        .lp-btn-primary:hover { background: var(--navy-deep); transform: translateY(-2px); box-shadow: 0 8px 24px rgba(10, 24, 56, .3); }
        .lp-btn-arrow { transition: transform .2s; }
        .lp-btn-primary:hover .lp-btn-arrow { transform: translateX(3px); }
        .lp-btn-ghost { color: var(--navy); border: 1px solid var(--navy); background: transparent; }
        .lp-btn-ghost:hover { background: var(--navy); color: #fff; }

        .lp-track-card {
          background: #fff; border-radius: 16px;
          padding: 28px;
          box-shadow: 0 12px 40px rgba(10, 24, 56, .12), 0 1px 2px rgba(10, 24, 56, .06);
          border: 1px solid rgba(10, 24, 56, .06);
        }
        .lp-track-label {
          font-size: .68rem; letter-spacing: .15em; font-weight: 700;
          color: var(--amber); margin-bottom: 14px;
        }
        .lp-track-row { display: flex; gap: 8px; }
        .lp-track-input {
          flex: 1; padding: 14px 16px;
          border: 1px solid var(--line); border-radius: 10px;
          font-family: 'JetBrains Mono', monospace, var(--sans);
          font-size: .95rem; color: var(--ink);
          transition: border-color .2s, box-shadow .2s;
        }
        .lp-track-input:focus { outline: none; border-color: var(--navy); box-shadow: 0 0 0 3px rgba(10, 24, 56, .08); }
        .lp-track-btn {
          padding: 14px 22px;
          background: var(--navy); color: #fff;
          border: 0; border-radius: 10px;
          font-weight: 500; font-size: .92rem;
          cursor: pointer; transition: background .2s;
        }
        .lp-track-btn:hover { background: var(--navy-deep); }
        .lp-track-hint { margin-top: 12px; font-size: .8rem; color: var(--muted); }
        .lp-track-hint a { color: var(--navy); text-decoration: underline; }

        .lp-why {
          padding: 100px 32px;
          background: var(--navy);
          color: #fff;
        }
        .lp-section-inner { max-width: 1200px; margin: 0 auto; }
        .lp-h2 {
          font-family: var(--serif); font-weight: 400;
          font-size: clamp(2rem, 4vw, 3.2rem);
          line-height: 1.1; letter-spacing: -.015em;
          color: #fff; margin: 0 0 60px;
        }
        .lp-h2-light { color: var(--cream); }
        .lp-why-grid {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 48px 56px;
        }
        .lp-why-item {
          padding-top: 24px;
          border-top: 1px solid rgba(255, 255, 255, .14);
        }
        .lp-why-num {
          font-family: var(--serif); font-size: 1.4rem;
          color: var(--amber); margin-bottom: 14px;
          letter-spacing: -.02em;
        }
        .lp-why-item h3 {
          font-family: var(--sans);
          font-size: 1.18rem; font-weight: 600;
          color: #fff; margin: 0 0 10px;
        }
        .lp-why-item p {
          font-size: .95rem; color: rgba(255, 255, 255, .68);
          margin: 0; line-height: 1.6;
        }

        .lp-quote {
          padding: 100px 32px;
          background: var(--cream);
        }
        .lp-quote-inner {
          display: grid; grid-template-columns: 1fr 1.1fr; gap: 80px;
          align-items: center;
        }
        .lp-quote-copy h2 { color: var(--navy); margin-bottom: 24px; }
        .lp-quote-lede { font-size: 1.05rem; color: var(--muted); max-width: 440px; }

        .lp-quote-card {
          background: #fff; border-radius: 16px;
          padding: 36px;
          box-shadow: 0 12px 40px rgba(10, 24, 56, .08), 0 1px 2px rgba(10, 24, 56, .04);
          border: 1px solid rgba(10, 24, 56, .06);
        }
        .lp-quote-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
        .lp-q-field { display: flex; flex-direction: column; gap: 6px; }
        .lp-q-field-full { margin-bottom: 18px; }
        .lp-q-field label {
          font-size: .72rem; letter-spacing: .1em; font-weight: 600;
          color: var(--muted); text-transform: uppercase;
        }
        .lp-q-field input, .lp-q-field select {
          padding: 12px 14px; border: 1px solid var(--line);
          border-radius: 8px; font-size: .95rem;
          font-family: var(--sans); color: var(--ink);
          background: #fff;
          transition: border-color .2s;
        }
        .lp-q-field input:focus, .lp-q-field select:focus {
          outline: none; border-color: var(--navy);
        }
        .lp-quote-btn {
          width: 100%; padding: 14px;
          background: var(--amber); color: var(--navy);
          border: 0; border-radius: 10px;
          font-weight: 600; font-size: .98rem;
          cursor: pointer; transition: background .2s, transform .2s;
        }
        .lp-quote-btn:hover { background: var(--amber-soft); transform: translateY(-1px); }
        .lp-quote-btn:disabled { opacity: .6; cursor: not-allowed; }

        .lp-quote-result {
          margin-top: 24px; padding-top: 24px;
          border-top: 1px dashed var(--line);
          display: flex; flex-direction: column; gap: 8px;
        }
        .lp-qr-line {
          display: flex; justify-content: space-between;
          font-size: .92rem; color: var(--muted);
        }
        .lp-qr-total {
          margin-top: 8px; padding-top: 14px;
          border-top: 1px solid var(--line);
          font-size: 1.15rem; font-weight: 700; color: var(--navy);
        }
        .lp-qr-book {
          margin-top: 16px; padding: 12px 18px;
          background: var(--navy); color: #fff !important;
          text-align: center; border-radius: 10px;
          font-weight: 500; font-size: .92rem;
          transition: background .2s;
        }
        .lp-qr-book:hover { background: var(--navy-deep); }

        .lp-footer {
          background: var(--navy-deep); color: rgba(255, 255, 255, .7);
          padding: 60px 32px 24px;
        }
        .lp-footer-inner {
          max-width: 1200px; margin: 0 auto;
          display: grid; grid-template-columns: 1.3fr 2fr; gap: 60px;
          padding-bottom: 40px;
          border-bottom: 1px solid rgba(255, 255, 255, .12);
        }
        .lp-footer-brand .lp-brand-name { color: #fff; }
        .lp-footer-tag { font-size: .85rem; margin-top: 12px; max-width: 260px; }
        .lp-footer-cols { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; }
        .lp-footer-cols h4 {
          font-size: .85rem; font-weight: 600; color: #fff;
          margin: 0 0 16px;
        }
        .lp-footer-cols a {
          display: block; padding: 5px 0;
          font-size: .9rem; color: rgba(255, 255, 255, .55);
          transition: color .2s;
        }
        .lp-footer-cols a:hover { color: #fff; }
        .lp-footer-bottom {
          max-width: 1200px; margin: 24px auto 0;
          display: flex; justify-content: space-between;
          font-size: .8rem; color: rgba(255, 255, 255, .4);
        }

        @media (max-width: 900px) {
          .lp-hero-grid, .lp-quote-inner { grid-template-columns: 1fr; gap: 40px; }
          .lp-why-grid { grid-template-columns: 1fr 1fr; gap: 32px 24px; }
          .lp-footer-inner { grid-template-columns: 1fr; gap: 40px; }
          .lp-nav-links { gap: 14px; font-size: .85rem; }
          .lp-nav-links a:not(.lp-nav-cta):not(.lp-nav-signin) { display: none; }
        }
        @media (max-width: 600px) {
          .lp-hero { padding: 50px 22px 70px; }
          .lp-why, .lp-quote { padding: 70px 22px; }
          .lp-why-grid { grid-template-columns: 1fr; }
          .lp-footer-cols { grid-template-columns: 1fr 1fr; }
          .lp-footer-bottom { flex-direction: column; gap: 8px; text-align: center; }
          .lp-quote-row { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
